import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Typography, Radio } from 'antd';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { CreditCardOutlined } from '@ant-design/icons';
import RCTable from 'components/custom-table/RCTable';
import {
	getAdvanceColumnsConfig,
	getFinalColumnsConfig,
} from 'utils/table-config/TripPaymentTableConfig';

const { Title, Text } = Typography;

const TableWrapper = styled.div`
	.ant-table-thead > tr > th {
		background-color: #1f3e5a !important;
		color: #ffffff;
		border-bottom: none;
	}
	.ant-table-tbody > tr > td {
		border-bottom: 1px solid #f0f0f0;
	}
	.ant-pagination {
		display: none !important;
	}
`;
const PaymentIconWrapper = styled.div`
	background-color: #e6f7ff;
	border-radius: 20%;
	width: 28px;
	height: 28px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-right: 12px;
	flex-shrink: 0;
`;

const BORDER_STYLE_SOLID = '1px solid #f0f0f0';

const VENDOR_VIEW = 'vendor';
const CLIENT_VIEW = 'client';

const emptyTable = { rows: [], count: 0 };

export default function PaymentDetailsCard({ payments, invoiceDetails, clientRate }) {
	// Toggle between the vendor and client payment views (vendor is the default).
	const [view, setView] = useState(VENDOR_VIEW);

	const [advanceRowData, setAdvanceRowData] = useState(emptyTable);
	const [advancePaginate, setAdvancePaginate] = useState({
		limit: 10,
		currentPage: 1,
		offset: 0,
	});

	const [finalRowData, setFinalRowData] = useState(emptyTable);
	const [finalPaginate, setFinalPaginate] = useState({ limit: 10, currentPage: 1, offset: 0 });

	// Client side has no advance payment — only final invoice payment entries.
	const [clientFinalRowData, setClientFinalRowData] = useState(emptyTable);
	const [clientFinalPaginate, setClientFinalPaginate] = useState({
		limit: 10,
		currentPage: 1,
		offset: 0,
	});

	useEffect(() => {
		setAdvanceRowData({
			rows: payments?.payment?.advance ? [payments.payment.advance] : [],
			count: payments?.payment?.advance ? 1 : 0,
		});

		const vendorFinalEntries = payments?.payment?.final?.invoice_payment_entries || [];
		setFinalRowData({ rows: vendorFinalEntries, count: vendorFinalEntries.length });

		// Preferred: the vendor-parity `client_final` block (has per-payment amount).
		const clientFinalEntries =
			payments?.payment?.client_final?.invoice_payment_entries ||
			payments?.payment?.client?.final?.invoice_payment_entries ||
			payments?.payment?.client?.invoice_payment_entries ||
			[];
		let clientRows = clientFinalEntries;
		if (!clientRows.length && payments?.clientRemaining?.length) {
			clientRows = payments.clientRemaining.map((r, i) => ({
				id: `crb-${i}`,
				paid_amount: r.paid_amount ?? null,
				utr_no: r.utr ?? r.utr_no ?? null,
				paid_at: r.paid_at ?? null,
				progress_percent: r.invoice_payment_progress ?? r.progress_percent ?? null,
				status:
					Number(r.invoice_payment_progress ?? r.progress_percent) >= 100
						? 'paid'
						: 'pending',
			}));
		}
		setClientFinalRowData({ rows: clientRows, count: clientRows.length });
	}, [payments]);

	const advanceColumns = getAdvanceColumnsConfig();
	const finalColumns = getFinalColumnsConfig();
	const clientDateOnly = (v) => {
		if (!v) return '-';
		const [datePart] = String(v).split('T');
		const p = datePart.split(' ')[0].split('-');
		return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : datePart;
	};
	const clientFinalColumns = finalColumns.map((col) =>
		col.key === 'paid_at'
			? { ...col, title: 'RECEIVED ON', render: (v) => clientDateOnly(v) }
			: col
	);

	const isClient = view === CLIENT_VIEW;

	// Final-payment summary row differs per view: vendor rate + vendor bill link, or
	// client rate + client invoice link.
	const rateLabel = isClient ? 'Client Rate:' : 'Vendor Rate:';
	const rateValue = isClient ? clientRate : payments?.outstanding;
	const clientInvoiceId = invoiceDetails?.client_invoice_id;
	const clientInvoiceLabel =
		invoiceDetails?.client_invoice_display_id || invoiceDetails?.client_invoice_id;

	const renderInvoiceLink = () => {
		if (isClient) {
			return clientInvoiceId ? (
				<Link
					to={`/client-invoices/${clientInvoiceId}`}
					style={{ fontSize: '15px', color: '#1890ff', textDecoration: 'underline' }}
				>
					{clientInvoiceLabel}
				</Link>
			) : (
				<Text strong style={{ fontSize: '15px' }}>
					-
				</Text>
			);
		}
		return invoiceDetails?.vendor_bill_id ? (
			<Link
				to={`/vendor-bill-invoice/${invoiceDetails.vendor_bill_id}`}
				style={{ fontSize: '15px', color: '#1890ff', textDecoration: 'underline' }}
			>
				{invoiceDetails.invoice_id}
			</Link>
		) : (
			<Text strong style={{ fontSize: '15px' }}>
				-
			</Text>
		);
	};

	return (
		<Card
			bodyStyle={{ padding: '0px' }}
			style={{ borderRadius: '8px', marginBottom: '16px', border: BORDER_STYLE_SOLID }}
		>
			<div
				style={{
					padding: '16px 24px',
					borderBottom: BORDER_STYLE_SOLID,
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					gap: '12px',
					flexWrap: 'wrap',
				}}
			>
				<Title
					level={5}
					style={{ margin: 0, display: 'flex', gap: '8px', alignItems: 'center' }}
				>
					<PaymentIconWrapper>
						<CreditCardOutlined
							style={{ color: '#698fc4ff', padding: '6px', borderRadius: '6px' }}
						/>
					</PaymentIconWrapper>
					Payment Details
				</Title>

				<Radio.Group
					value={view}
					onChange={(e) => setView(e.target.value)}
					optionType="button"
					buttonStyle="solid"
					size="small"
				>
					<Radio.Button value={VENDOR_VIEW}>Vendor</Radio.Button>
					<Radio.Button value={CLIENT_VIEW}>Client</Radio.Button>
				</Radio.Group>
			</div>

			<div style={{ padding: '24px' }}>
				{/* Advance payment — vendor only (client has no advance). */}
				{!isClient && (
					<>
						<Title
							level={5}
							style={{ fontSize: '12px', color: '#595959', marginBottom: '16px' }}
						>
							ADVANCE PAYMENT
						</Title>
						<TableWrapper style={{ marginBottom: '32px' }}>
							<RCTable
								columns={advanceColumns}
								tableDatas={[advanceRowData, setAdvanceRowData]}
								togglePaginate={[advancePaginate, setAdvancePaginate]}
								hasPagination={false}
								pagination={false}
								scroll={{ y: 300 }}
								rowKey="id"
							/>
						</TableWrapper>
					</>
				)}

				<Title
					level={5}
					style={{ fontSize: '12px', color: '#595959', marginBottom: '16px' }}
				>
					FINAL PAYMENT
				</Title>
				<div
					style={{
						border: BORDER_STYLE_SOLID,
						borderRadius: '8px',
						padding: '16px 24px',
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						marginBottom: '16px',
					}}
				>
					<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<Text type="secondary" style={{ fontSize: '15px' }}>
							{rateLabel}
						</Text>
						<Text strong style={{ fontSize: '15px' }}>
							₹ {rateValue || '0'}
						</Text>
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<Text type="secondary" style={{ fontSize: '15px' }}>
							Invoice ID :
						</Text>
						{renderInvoiceLink()}
					</div>
				</div>

				<TableWrapper>
					{isClient ? (
						<RCTable
							columns={clientFinalColumns}
							tableDatas={[clientFinalRowData, setClientFinalRowData]}
							togglePaginate={[clientFinalPaginate, setClientFinalPaginate]}
							hasPagination={false}
							pagination={false}
							scroll={{ y: 300 }}
							rowKey="id"
						/>
					) : (
						<RCTable
							columns={finalColumns}
							tableDatas={[finalRowData, setFinalRowData]}
							togglePaginate={[finalPaginate, setFinalPaginate]}
							hasPagination={false}
							pagination={false}
							scroll={{ y: 300 }}
							rowKey="id"
						/>
					)}
				</TableWrapper>
			</div>
		</Card>
	);
}

PaymentDetailsCard.propTypes = {
	payments: PropTypes.object,
	invoiceDetails: PropTypes.object,
	clientRate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
